import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import React, { useCallback } from 'react';
import classnames from 'clsx';
import CustomizableCalendarDay from 'react-dates/lib/components/CustomizableCalendarDay';
import { SingleDatePicker } from 'react-dates';
import moment from 'moment';

import { Button } from 'ui-components/components/button';
import { noop } from 'ui-components/default-props/noop';

import { twelveHourUserSettingsNoTimeZoneUnpadded } from '../../../../constants/timezone-format';
import { MM_DD_YYYY } from '../../../../constants/date-format';
import { TimePicker } from './time-picker';

import '../../../styles/lib-overrides/react-dates.css';
import style from '../calendar-date-picker.module.css';

const NavPrev = <FontAwesomeIcon className={classnames(style['icon-wrapper'], style['left-arrow'])} icon={faArrowLeft} />;
const NavNext = <FontAwesomeIcon className={classnames(style['icon-wrapper'], style['right-arrow'])} icon={faArrowRight} />;

/**
 * CalendarDateTimePicker component
 * This is our wrapper of the react-dates SingleDatePicker
 * @param {object} props
 * @param {boolean} [props.allowPastDates] - whether to allow past dates
 * @param {string} [props.className] - the class name of the component
 * @param {string} [props.dateFormat] - the format to display the date in
 * @param {(timestamp: string) => void} props.onChange - function to call when the date time is changed
 * @param {number} [props.verticalSpacing] - the vertical spacing of the component
 * @param {string} [props.timeFormat] - the format to display the time in
 * @param {string} props.utcValue - the value of the date time picker in ISO format with UTC timezone
 * @param {string} props.sellerTimeZone - the timezone of the seller
 * @returns {React.ReactElement} - the calendar date time picker component
 */
export const CalendarDateTimePicker = ({
  allowPastDates = false,
  className = '',
  dateFormat = MM_DD_YYYY,
  onChange,
  verticalSpacing = -20,
  timeFormat = twelveHourUserSettingsNoTimeZoneUnpadded,
  utcValue, // iso string in utc timezone
  sellerTimeZone,
}) => {
  const isOutsideRange = useCallback(
    (day) => {
      return allowPastDates ? false : moment().tz(sellerTimeZone).isAfter(day, 'day');
    },
    [allowPastDates, sellerTimeZone],
  );

  const momentSelectedDate = utcValue ? moment.tz(utcValue, sellerTimeZone) : undefined;

  const handleDateChange = useCallback(
    (newDatetime) => {
      if (!newDatetime) return;
      // new date seems to be at 18:00, so we need to set it to the start of the day
      newDatetime.tz(sellerTimeZone);

      // if momentSelectedDate is not midnight keep the time on the new date
      if (momentSelectedDate && momentSelectedDate.hour() !== 0) {
        newDatetime.hour(momentSelectedDate.hour());
        newDatetime.minute(momentSelectedDate.minute());

        onChange(newDatetime.format());

        return;
      }

      // otherwise, set the time to midnight
      const startOfNewDate = newDatetime.startOf('day');
      const startOfNewDateInUtc = startOfNewDate.format();
      onChange(startOfNewDateInUtc);
    },
    [onChange, sellerTimeZone, momentSelectedDate],
  );

  const handleTimeChange = useCallback(
    (newDatetime) => {
      // newDatetime is in the seller's timezone
      const newDateInUtc = newDatetime.format();
      onChange(newDateInUtc);
    },
    [onChange],
  );

  return (
    <div className={classnames(style['calendar-date-picker'], 'components-date-picker', className)}>
      <SingleDatePicker
        date={momentSelectedDate}
        daySize={30}
        focused
        hideKeyboardShortcutsPanel
        initialVisibleMonth={() => {
          return !!momentSelectedDate && momentSelectedDate.isValid() ? momentSelectedDate : moment().tz(sellerTimeZone);
        }}
        isOutsideRange={isOutsideRange}
        keepOpenOnDateSelect
        navNext={NavNext}
        navPrev={NavPrev}
        numberOfMonths={1}
        onDateChange={handleDateChange}
        onFocusChange={noop} // NOTE: do not change this function, it will cause this component to not work. This needs to be a noop
        renderCalendarDay={({ onDayClick, key, day, daySize, tabIndex }) => {
          const dayStyle = {
            width: daySize,
            height: daySize,
          };
          if (!day) {
            return (
              <CustomizableCalendarDay
                key={key}
                day={day}
                daySize={daySize}
                renderDayContents={() => <div className={style['day-placeholder']} style={dayStyle} />}
                tabIndex={tabIndex}
              />
            );
          }
          const selected = momentSelectedDate && day.format(dateFormat) === momentSelectedDate.format(dateFormat);
          const disabled = isOutsideRange(day);

          return (
            <CustomizableCalendarDay
              key={key}
              day={day}
              daySize={daySize}
              defaultStyles={{}}
              hoveredSpanStyles={{}}
              onDayClick={onDayClick}
              renderDayContents={() => (
                <Button
                  key={key}
                  className={classnames(style['day-button'], {
                    [style.selected]: selected,
                  })}
                  disabled={disabled}
                  onClick={() => onDayClick(day)}
                  style={dayStyle}
                  theme='unstyled'
                >
                  {day.date()}
                </Button>
              )}
              tabIndex={tabIndex}
            />
          );
        }}
        renderCalendarInfo={() => <TimePicker datetime={momentSelectedDate} onChange={handleTimeChange} timeFormat={timeFormat} />}
        verticalSpacing={Math.max(verticalSpacing, 1)}
      />
    </div>
  );
};
